'use client';

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { roipService } from '@/lib/api/roipService';
import { ROIPEntry } from '@/types/roip';
import { Button } from '@/components/ui/Button';
import ErrorMessage from '@/components/common/ErrorMessage';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Switch } from '@/components/ui/Switch';
import { Badge } from '@/components/ui/Badge';
import { useAuth } from '@/hooks/useAuth';

const CRITERIOS_BASE = [
  { value: 'roi', label: 'Reporte de Operaciones Inusuales', field: 'roi' },
  { value: 'reportadoPor', label: 'Reportado por', field: 'reportadoPor' },
  { value: 'reporteEstatus', label: 'Estatus', field: 'reporteEstatus' },
  { value: 'fechaReporte', label: 'Fecha de Reporte', field: 'fechaReporte' },
  { value: 'fechaDictaminacion', label: 'Fecha de Dictaminación', field: 'fechaDictaminacion' },
  { value: 'fechaReporteSITI', label: 'Fecha Reporte en SITI', field: 'fechaReporteSITI' },
];

const OperacionesInternasPreocupantesManagement: React.FC = () => {
  const { user, isFieldAllowed } = useAuth();
  const canRoi = isFieldAllowed('roi');
  const canReportadoPor = isFieldAllowed('reportadoPor');
  const canReporteEstatus = isFieldAllowed('reporteEstatus');
  const canFechaReporte = isFieldAllowed('fechaReporte');
  const canFechaDictaminacion = isFieldAllowed('fechaDictaminacion');
  const canFechaReporteSITI = isFieldAllowed('fechaReporteSITI');
  const canDocumentosROIP = isFieldAllowed('documentosROIP');
  const [items, setItems] = useState<ROIPEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const criterios = useMemo(() => {
    const allowed: Record<string, boolean> = { roi: canRoi, reportadoPor: canReportadoPor, reporteEstatus: canReporteEstatus, fechaReporte: canFechaReporte, fechaDictaminacion: canFechaDictaminacion, fechaReporteSITI: canFechaReporteSITI };
    return CRITERIOS_BASE.filter(c => allowed[c.field] !== false);
  }, [canRoi, canReportadoPor, canReporteEstatus, canFechaReporte, canFechaDictaminacion, canFechaReporteSITI]);
  const [criterio, setCriterio] = useState('roi');
  const [valor, setValor] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [editingItem, setEditingItem] = useState<ROIPEntry | null>(null);
  const [deletingItem, setDeletingItem] = useState<ROIPEntry | null>(null);

  const [formData, setFormData] = useState({
    roi: '',
  });

  const load = useCallback(async () => {
    if (!user?.sub) {
      setError('Usuario no autenticado');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const res = await roipService.getAll(user.sub);
      if (res.data) {
        setItems(res.data);
      } else {
        setError(res.error || 'Error al cargar ROIPs');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar ROIPs');
      console.error('Error fetching ROIPs:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user?.sub]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (criterios.length > 0 && !criterios.some(c => c.value === criterio)) {
      setCriterio(criterios[0].value);
    }
  }, [criterios, criterio]);

  const filtered = useMemo(() => {
    if (!valor.trim()) return items;
    const term = valor.toLowerCase();
    return items.filter((it) => {
      const val = String((it as any)[criterio] || '');
      return val.toLowerCase().includes(term);
    });
  }, [items, criterio, valor]);

  const handleCreate = async () => {
    if (!user?.sub || !user?.name) {
      setError('Usuario no autenticado');
      return;
    }

    if (!formData.roi || !formData.roi.trim()) {
      setError('El campo "Reporte de Operaciones Inusuales" es requerido');
      return;
    }

    setError(null);
    try {
      const res = await roipService.create({
        roi: formData.roi,
        ownerID: user.sub,
        userName: user.name
      });
      
      if (res.data) {
        setItems((prev) => [res.data!, ...prev]);
        setIsModalOpen(false);
        setFormData({ roi: '' });
      } else {
        setError(res.error || 'No se pudo guardar ROIP');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'No se pudo guardar ROIP');
      console.error('Error creating ROIP:', err);
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleToggleFechaDictaminacion = async (item: ROIPEntry, enabled: boolean) => {
    if (!user?.sub || !user?.name || !item.id) return;
    
    setError(null);
    try {
      const res = await roipService.toggleFechaDictaminacion(item.id, enabled, user.sub, user.name);
      if (res.data) {
        setItems(prev => prev.map(i => i.id === item.id ? res.data! : i));
      } else {
        setError(res.error || 'No se pudo actualizar la fecha de dictaminación');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'No se pudo actualizar la fecha de dictaminación');
      console.error('Error toggling fechaDictaminacion:', err);
    }
  };

  const handleToggleFechaReporteSITI = async (item: ROIPEntry, enabled: boolean) => {
    if (!user?.sub || !user?.name || !item.id) return;
    
    setError(null);
    try {
      const res = await roipService.toggleFechaReporteSITI(item.id, enabled, user.sub, user.name);
      if (res.data) {
        setItems(prev => prev.map(i => i.id === item.id ? res.data! : i));
      } else {
        setError(res.error || 'No se pudo actualizar la fecha de reporte SITI');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'No se pudo actualizar la fecha de reporte SITI');
      console.error('Error toggling fechaReporteSITI:', err);
    }
  };

  const handleUpdateDocumentos = async (item: ROIPEntry, value: string) => {
    if (!item.id) return;
    
    setError(null);
    try {
      const res = await roipService.update(item.id, { documentosROIP: value });
      if (res.data) {
        setItems(prev => prev.map(i => i.id === item.id ? res.data! : i));
      } else {
        setError(res.error || 'No se pudo actualizar los documentos');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'No se pudo actualizar los documentos');
      console.error('Error updating documentos:', err);
    }
  };

  const handleDelete = async () => {
    if (!deletingItem?.id) return;
    
    setError(null);
    try {
      const res = await roipService.delete(deletingItem.id);
      if (res.data) {
        setItems(prev => prev.filter(i => i.id !== deletingItem.id));
        setDeletingItem(null);
        // Si estaba expandido, cerrarlo
        setExpandedItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(deletingItem.id!);
          return newSet;
        });
      } else {
        setError(res.error || 'No se pudo eliminar el ROIP');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'No se pudo eliminar el ROIP');
      console.error('Error deleting ROIP:', err);
    }
  };

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '—';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('es-MX', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const getEstatusBadgeVariant = (estatus?: string): 'success' | 'danger' | 'warning' | 'info' | 'default' => {
    switch (estatus?.toLowerCase()) {
      case 'abierto':
        return 'info';
      case 'dictaminado':
        return 'warning';
      case 'reportado':
        return 'success';
      default:
        return 'default';
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Operaciones Internas Preocupantes</h1>
          <p className="text-sm text-gray-500 mt-1">
            Gestión de reportes de operaciones inusuales con control de estatus y fechas de dictaminación.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={load}>Recargar</Button>
          {canRoi && (
            <Button onClick={() => setIsModalOpen(true)}>Agregar ROIP</Button>
          )}
        </div>
      </div>

      {error && <ErrorMessage message={error} />}

      {criterios.length > 0 && (
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Criterio</label>
            <select
              className="w-full px-3 py-2 border rounded-lg shadow-sm border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              value={criterios.some(c => c.value === criterio) ? criterio : criterios[0]?.value ?? ''}
              onChange={(e) => setCriterio(e.target.value)}
            >
              {criterios.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <Input label="Valor" value={valor} onChange={(e) => setValor(e.target.value)} />
          <Button variant="secondary" onClick={() => setValor('')}>Limpiar</Button>
        </div>
      </div>
      )}

      <div className="space-y-4">
        {filtered.map((item) => {
          const isExpanded = expandedItems.has(item.id || '');
          const hasFechaDictaminacion = Boolean(
            item.fechaDictaminacion && 
            (typeof item.fechaDictaminacion === 'string' ? item.fechaDictaminacion.trim() !== '' : true)
          );
          const hasFechaReporteSITI = Boolean(
            item.fechaReporteSITI && 
            (typeof item.fechaReporteSITI === 'string' ? item.fechaReporteSITI.trim() !== '' : true)
          );
          
          return (
            <div
              key={item.id}
              className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
            >
              {/* Header - Siempre visible */}
              <div
                className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => item.id && toggleExpand(item.id)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      {canReporteEstatus && (
                        <Badge variant={getEstatusBadgeVariant(item.reporteEstatus)}>
                          {item.reporteEstatus || 'abierto'}
                        </Badge>
                      )}
                      {canReportadoPor && (
                        <span className="text-xs text-gray-500">
                          Reportado por: {item.reportadoPor || '—'}
                        </span>
                      )}
                      {canFechaReporte && (
                        <span className="text-xs text-gray-500">
                          Fecha: {formatDate(item.fechaReporte)}
                        </span>
                      )}
                    </div>
                    {canRoi && (
                      <p className="text-sm text-gray-700 line-clamp-2">
                        {item.roi || '—'}
                      </p>
                    )}
                  </div>
                  <div className="flex-shrink-0">
                    <svg
                      className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Detalle expandible */}
              {isExpanded && (
                <div className="border-t border-gray-100 p-4 space-y-4">
                  {canRoi && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reporte de Operaciones Inusuales
                    </label>
                    <textarea
                      value={item.roi || ''}
                      readOnly
                      className="w-full px-3 py-2 border rounded-lg shadow-sm border-gray-300 bg-gray-50 text-gray-700 cursor-not-allowed"
                      rows={4}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Este campo no se puede modificar después de creado
                    </p>
                  </div>
                  )}

                  {(canFechaDictaminacion || canFechaReporteSITI) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {canFechaDictaminacion && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-gray-700">
                          Fecha de Dictaminación
                        </label>
                        <Switch
                          checked={hasFechaDictaminacion}
                          onChange={(enabled) => handleToggleFechaDictaminacion(item, enabled)}
                          color="blue"
                        />
                      </div>
                      <div className="text-sm text-gray-600">
                        {formatDate(item.fechaDictaminacion)}
                      </div>
                    </div>
                    )}
                    {canFechaReporteSITI && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-gray-700">
                          Fecha Reporte SITI
                        </label>
                        <Switch
                          checked={hasFechaReporteSITI}
                          onChange={(enabled) => handleToggleFechaReporteSITI(item, enabled)}
                          color="green"
                        />
                      </div>
                      <div className="text-sm text-gray-600">
                        {formatDate(item.fechaReporteSITI)}
                      </div>
                    </div>
                    )}
                  </div>
                  )}

                  {canDocumentosROIP && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Link Documentos
                    </label>
                    <div className="flex gap-2">
                      <Input
                        value={item.documentosROIP || ''}
                        onChange={(e) => {
                          const newItem = { ...item, documentosROIP: e.target.value };
                          setItems(prev => prev.map(i => i.id === item.id ? newItem : i));
                        }}
                        placeholder="Ingrese link o referencia a documentos relacionados"
                      />
                      <Button
                        variant="secondary"
                        onClick={() => handleUpdateDocumentos(item, item.documentosROIP || '')}
                      >
                        Guardar
                      </Button>
                    </div>
                  </div>
                  )}

                  {(canReportadoPor || canFechaReporte) && (
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {canReportadoPor && (
                    <div>
                      <span className="text-gray-500">Reportado por:</span>
                      <span className="ml-2 font-medium">{item.reportadoPor || '—'}</span>
                    </div>
                    )}
                    {canFechaReporte && (
                    <div>
                      <span className="text-gray-500">Fecha de Reporte:</span>
                      <span className="ml-2 font-medium">{formatDate(item.fechaReporte)}</span>
                    </div>
                    )}
                  </div>
                  )}

                  {/* Botón de eliminar */}
                  <div className="flex justify-end pt-4 border-t border-gray-200">
                    <Button
                      variant="secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeletingItem(item);
                      }}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Eliminar ROIP
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {filtered.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center text-gray-500">
            Sin registros
          </div>
        )}
      </div>

      {/* Modal para crear nuevo ROIP */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Agregar ROIP">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reporte de Operaciones Inusuales <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.roi}
              onChange={(e) => setFormData({ ...formData, roi: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg shadow-sm border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              rows={6}
              placeholder="Describa la operación inusual..."
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Este campo no se podrá modificar después de crear el ROIP
            </p>
          </div>
          
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => {
              setIsModalOpen(false);
              setFormData({ roi: '' });
            }}>
              Cancelar
            </Button>
            <Button onClick={handleCreate}>Guardar</Button>
          </div>
        </div>
      </Modal>

      {/* Modal de confirmación para eliminar */}
      <Modal
        isOpen={deletingItem !== null}
        onClose={() => setDeletingItem(null)}
        confirmOnExit={false}
        title="Confirmar Eliminación"
      >
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <h3 className="text-sm font-semibold text-red-800 mb-1">
                  ¿Está seguro de que desea eliminar este ROIP?
                </h3>
                <p className="text-sm text-red-700">
                  Esta acción no se puede deshacer. El ROIP será eliminado permanentemente.
                </p>
              </div>
            </div>
          </div>

          {deletingItem && (
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              {canRoi && (
              <p className="text-sm text-gray-600 mb-1">
                <span className="font-medium">Reporte:</span>
              </p>
              )}
              {canRoi && (
              <p className="text-sm text-gray-800 line-clamp-3">
                {deletingItem.roi || '—'}
              </p>
              )}
              {(canReportadoPor || canFechaReporte) && (
              <div className="mt-2 text-xs text-gray-500">
                {canReportadoPor && <span>Reportado por: {deletingItem.reportadoPor || '—'}</span>}
                {canReportadoPor && canFechaReporte && ' '}
                {canFechaReporte && <span>Fecha: {formatDate(deletingItem.fechaReporte)}</span>}
              </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button 
              variant="secondary" 
              onClick={() => setDeletingItem(null)}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default OperacionesInternasPreocupantesManagement;
